/***************************************
 Helper utils for responsibility support
 ***************************************/

import {onUnmounted, ref, Ref, watch} from "vue";
import {debounce} from "@/utils/Debounce.ts";

/**
 * Find nearest parent which can be considered "container".
 * By the container is meant a element which meets one of these conditions:
 *  - element with CSS container-type: inline-size
 *  - element with class .content || .object-content || .subview-content || .ui-container
 * @param element
 */
export function findContainer(element: HTMLElement):HTMLElement|null {
	let current:HTMLElement = element;
	do {
		const clist = current.classList;
		if (clist.contains("content") /* || clist.contains("object-content") || clist.contains("subview-content") */
			|| clist.contains("ui-container") || clist.contains("is-container")) return current;
		const styles = getComputedStyle(current);
		const containerType = styles.getPropertyValue('container-type');
		if (containerType === 'inline-size') return current;  // Found the container
		current = current.parentElement;
	} while (current);
	// Return null if no container with container-type: inline-size is found
	return null;
}

class Observatory {
	protected widths:Ref<number>[];
	public width: (...args: Parameters<(w: number) => void>) => void;
	public container: HTMLElement;

	constructor(container:HTMLElement) {
		this.widths = [];
		this.container = container;

		this.width = debounce((w:number) => {
			for (let wr of this.widths) {
				wr.value = w;
			}
		}, 50);
	}

	observe(width:Ref<number>):void {
		const empty = this.widths.length === 0;
		this.widths.push(width);
		if (empty) this.start();
	}

	unobserve(width:Ref<number>):boolean {
		const idx = this.widths.findIndex((w) => w === width);
		if (idx >= 0) {
			this.widths.splice(idx, 1);
			if (this.widths.length === 0) {
				this.stop();
				return true;
			}
			return false;
		} else throw new Error("Observatory didn't found ref to unobserve");
	}

	protected stop() {
	}

	protected start() {
	}
}


class ContainerObservatory extends Observatory {
	private observer: ResizeObserver;
	private handler: (entries: ResizeObserverEntry[]) => void;

	constructor(container:HTMLElement) {
		super(container);
		this.handler = this.containerResized.bind(this);
	}

	containerResized(entries: ResizeObserverEntry[]) {
		if (entries.length > 0) {
			this.width(Math.floor(entries[0].contentRect.width));
		}
	}

	protected stop() {
		this.observer.unobserve(this.container);
		this.observer = null;
	}

	protected start() {
		this.observer = new ResizeObserver(this.handler);
		this.observer.observe(this.container);
	}
}


class WindowObservatory extends Observatory {
	private handler: () => void;

	constructor() {
		super(document.documentElement);
		this.handler = this.updateWidth.bind(this);
	}

	updateWidth() {
		this.width(document.documentElement.clientWidth);
	}

	protected stop() {
		window.removeEventListener("resize", this.handler);
	}

	protected start() {
		window.addEventListener("resize", this.handler);
	}
}


const registry: Map<HTMLElement, Observatory> = new Map();

function dumpElm(elm:HTMLElement):string {
	if (elm == null) return "null";
	let b = "<" + elm.tagName;
	for (let a of elm.attributes) {
		b += " " + a.name + "='" + a.value + "'";
	}
	b += ">";
	return b;
}

export function observeWidth(element:Ref<HTMLElement>):Ref<number> {
	const width = ref<number>(document.documentElement.clientWidth);

	let observatory: Observatory = null;

	onUnmounted(() => {
		if (observatory != null) {
			if (observatory.unobserve(width)) registry.delete(observatory.container);
			observatory = null;
		}
	});

	const unwatch = watch(element, (child:HTMLElement|null) => {
		if (child) {
			let container = findContainer(child);
			if (container) {
				observatory = registry.get(container);
				if (!observatory) {
					observatory = new ContainerObservatory(container);
					registry.set(container, observatory);
				}
			} else {
				observatory = registry.get(document.documentElement);
				if (!observatory) {
					observatory = new WindowObservatory();
					registry.set(document.documentElement, observatory);
				}
			}
			observatory.observe(width);
			unwatch();
		}
	});

	return width;
}